/**
 * React hooks for posts
 * Integrates with TanStack Query for caching and state management
 */

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as postsService from '../services/posts';
import { useAuth } from './useAuth';

// Query keys for consistent cache management
export const postKeys = {
  all: ['posts'] as const,
  lists: () => [...postKeys.all, 'list'] as const,
  list: (filters: string) => [...postKeys.lists(), { filters }] as const,
  details: () => [...postKeys.all, 'detail'] as const,
  detail: (id: number) => [...postKeys.details(), id] as const,
  infinite: () => [...postKeys.all, 'infinite'] as const,
  pending: () => [...postKeys.all, 'pending'] as const,
  pendingCount: () => [...postKeys.all, 'pending-count'] as const,
};

/**
 * Fetch all posts
 */
export function usePosts() {
  return useQuery({
    queryKey: postKeys.lists(),
    queryFn: postsService.getPosts,
  });
}

/**
 * Fetch posts with pagination (infinite query)
 */
export function useInfinitePosts() {
  return useInfiniteQuery({
    queryKey: postKeys.infinite(),
    queryFn: ({ pageParam }) =>
      postsService.getPostsPaginated(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

/**
 * Fetch a single post by ID
 */
export function usePost(id: number) {
  return useQuery({
    queryKey: postKeys.detail(id),
    queryFn: () => postsService.getPostById(id),
    enabled: !!id,
  });
}

/**
 * Create a new post
 */
export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      content,
      imageUrl,
    }: {
      userId: string;
      content: string;
      imageUrl?: string | null;
    }) => postsService.createPost(userId, content, imageUrl),
    onSuccess: async (newPost) => {
      // Notify admins of pending post
      await postsService.notifyAdminsOfPendingPost(newPost.id);

      // Invalidate posts list to refresh the feed
      queryClient.invalidateQueries({
        queryKey: postKeys.lists(),
      });
      // Also invalidate infinite query if it's being used
      queryClient.invalidateQueries({
        queryKey: postKeys.infinite(),
      });
      // Invalidate pending posts list
      queryClient.invalidateQueries({
        queryKey: postKeys.pending(),
      });
      queryClient.invalidateQueries({
        queryKey: postKeys.pendingCount(),
      });
    },
  });
}

/**
 * Update an existing post
 */
export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, content }: { postId: number; content: string }) =>
      postsService.updatePost(postId, content),
    onSuccess: (updatedPost) => {
      // Invalidate posts list to refresh the feed
      queryClient.invalidateQueries({
        queryKey: postKeys.lists(),
      });
      // Invalidate infinite query if it's being used
      queryClient.invalidateQueries({
        queryKey: postKeys.infinite(),
      });
      // Update the specific post detail cache
      queryClient.setQueryData(postKeys.detail(updatedPost.id), updatedPost);
    },
  });
}

/**
 * Delete a post
 */
export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) => postsService.deletePost(postId),
    onSuccess: (_, postId) => {
      // Invalidate posts list to refresh the feed
      queryClient.invalidateQueries({
        queryKey: postKeys.lists(),
      });
      // Invalidate infinite query if it's being used
      queryClient.invalidateQueries({
        queryKey: postKeys.infinite(),
      });
      // Invalidate pending posts list
      queryClient.invalidateQueries({
        queryKey: postKeys.pending(),
      });
      queryClient.invalidateQueries({
        queryKey: postKeys.pendingCount(),
      });
      // Remove the specific post detail from cache
      queryClient.removeQueries({
        queryKey: postKeys.detail(postId),
      });
    },
  });
}

/**
 * Fetch pending posts for moderation queue (admin only)
 */
export function usePendingPosts() {
  return useQuery({
    queryKey: postKeys.pending(),
    queryFn: postsService.getPendingPosts,
  });
}

/**
 * Fetch the number of pending posts for admin notification UI
 */
export function usePendingPostsCount(enabled: boolean = true) {
  return useQuery({
    queryKey: postKeys.pendingCount(),
    queryFn: postsService.getPendingPostsCount,
    enabled,
  });
}

/**
 * Approve a post (admin only)
 */
export function useApprovePost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (postId: number) => {
      if (!user?.id) {
        throw new Error('User must be authenticated to approve posts');
      }
      const approvedPost = await postsService.approvePost(postId, user.id);
      // Notify user of approval
      await postsService.notifyUserOfModerationResult(postId, 'approved');
      return approvedPost;
    },
    onSuccess: () => {
      // Invalidate all post queries
      queryClient.invalidateQueries({
        queryKey: postKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: postKeys.pendingCount(),
      });
    },
  });
}

/**
 * Reject a post with optional reason (admin only)
 */
export function useRejectPost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      postId,
      reason,
    }: {
      postId: number;
      reason?: string;
    }) => {
      if (!user?.id) {
        throw new Error('User must be authenticated to reject posts');
      }
      const rejectedPost = await postsService.rejectPost(
        postId,
        user.id,
        reason
      );
      // Notify user of rejection
      await postsService.notifyUserOfModerationResult(
        postId,
        'rejected',
        reason
      );
      return rejectedPost;
    },
    onSuccess: () => {
      // Invalidate all post queries
      queryClient.invalidateQueries({
        queryKey: postKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: postKeys.pendingCount(),
      });
    },
  });
}
